import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getPushStatus, enablePush, disablePush, sendTestPush } from "@/lib/push";

/**
 * Custom hook que encapsula:
 * - status do dispositivo (supported/permission/subscribed) via Web Push API
 * - cfg global (notify_new_order/notify_overdue) via GET/PUT /push/config
 * - ações enable/disable/test/saveCfg
 */
export default function usePushCard() {
  const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ notify_new_order: true, notify_overdue: true });
  const [savingCfg, setSavingCfg] = useState(false);

  const refresh = async () => setStatus(await getPushStatus());

  const loadCfg = async () => {
    try {
      const { data } = await api.get("/push/config");
      setCfg({
        notify_new_order: !!data?.notify_new_order,
        notify_overdue: !!data?.notify_overdue,
      });
    } catch (err) { console.warn("PushNotifyCard loadCfg:", err); }
  };

  useEffect(() => { refresh(); loadCfg(); }, []);

  const saveCfg = async (patch) => {
    const prev = cfg;
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSavingCfg(true);
    try {
      await api.put("/push/config", next);
      toast.success("Preferências de notificação salvas.");
    } catch (e) {
      toast.error("Falha ao salvar preferências.");
      setCfg(prev);
    } finally { setSavingCfg(false); }
  };

  const withBusy = (fn) => async () => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const onEnable = withBusy(async () => {
    try {
      await enablePush();
      toast.success("Notificações ativadas neste dispositivo.");
      await refresh();
    } catch (e) { toast.error(e?.message || "Falha ao ativar notificações."); }
  });

  const onDisable = withBusy(async () => {
    try {
      await disablePush();
      toast.success("Notificações desativadas neste dispositivo.");
      await refresh();
    } catch (e) { toast.error(e?.message || "Falha ao desativar notificações."); }
  });

  const onTest = withBusy(async () => {
    try {
      const r = await sendTestPush();
      if (r?.sent > 0) toast.success(`Notificação enviada para ${r.sent} dispositivo(s).`);
      else toast.warning("Nenhum dispositivo ativo recebeu a notificação de teste.");
    } catch (e) { toast.error("Falha ao enviar notificação de teste."); }
  });

  const denied = status.permission === "denied";
  const active = status.subscribed && status.permission === "granted";

  return { status, busy, savingCfg, cfg, denied, active, onEnable, onDisable, onTest, saveCfg };
}
